import React, { useEffect, useState } from 'react';
import supabase from '../supabase/supabase';
import { FiTrash2, FiHash, FiCheck, FiSave, FiHeart } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

export default function AdminPostList({ refreshKey }) {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [editingTags, setEditingTags] = useState({});
  const [editingVotes, setEditingVotes] = useState({});

  useEffect(() => {
    fetchData();
  }, [refreshKey]);

  const fetchData = async () => {
    const [postsRes, catsRes] = await Promise.all([
      supabase.from('posts_1715000000').select('*, categories_1715000000(name)').order('created_at', { ascending: false }),
      supabase.from('categories_1715000000').select('*').order('name')
    ]);
    
    if (postsRes.data) {
      setPosts(postsRes.data);
      const initialTags = {};
      const initialVotes = {};
      postsRes.data.forEach(post => {
        initialTags[post.id] = post.tags ? post.tags.join(', ') : '';
        initialVotes[post.id] = post.vote_count || 0;
      });
      setEditingTags(initialTags);
      setEditingVotes(initialVotes);
    }
    if (catsRes.data) setCategories(catsRes.data);
    setLoading(false);
  };

  const handleUpdateCategory = async (postId, newCategoryId) => {
    const { error } = await supabase
      .from('posts_1715000000')
      .update({ category_id: newCategoryId })
      .eq('id', postId);
    
    if (error) alert(`Error: ${error.message}`);
    else fetchData();
  };

  const handleSaveAll = async (postId) => {
    const tagsArray = editingTags[postId]
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);

    setProcessingId(postId);
    const { error } = await supabase
      .from('posts_1715000000')
      .update({ 
        tags: tagsArray,
        vote_count: parseInt(editingVotes[postId], 10) || 0
      })
      .eq('id', postId);

    setProcessingId(null);
    if (error) alert(`Error saving: ${error.message}`);
    else fetchData();
  };

  const handleDelete = async (id) => {
    if (!confirm('Trash this graphic forever?')) return;
    const { error: dbError } = await supabase.from('posts_1715000000').delete().eq('id', id);
    if (dbError) alert(`Error: ${dbError.message}`);
    else fetchData();
  };

  const handleGenerateAI = async (post) => {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    if (!apiKey) return alert("OpenAI API key missing!");
    
    setProcessingId(post.id);
    try {
      const categoryName = post.categories_1715000000?.name || "general";
      const promptText = `Analyze image and return 3 tags in JSON: {"tags": ["tag1", "tag2", "tag3"]}`;
      
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
        body: JSON.stringify({
          model: 'gpt-4o',
          response_format: { type: "json_object" },
          messages: [{ role: 'user', content: [{ type: 'text', text: promptText }, { type: 'image_url', image_url: { url: post.image_url } }]}]
        })
      });

      const data = await response.json();
      const aiResult = JSON.parse(data.choices[0].message.content);
      setEditingTags(prev => ({ ...prev, [post.id]: aiResult.tags.join(', ') }));
    } catch (err) {
      alert(`AI Failed: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) return <div className="font-bold text-retro-brown bg-white p-8 rounded-3xl border-4 border-retro-brown shadow-retro">Loading database...</div>;

  return (
    <div className="bg-white p-6 rounded-3xl shadow-retro border-4 border-retro-brown overflow-hidden">
      <div className="flex justify-between items-center mb-6 px-2">
        <h2 className="font-display text-2xl tracking-wider text-retro-brown uppercase">Manual Content Control</h2>
      </div>

      <div className="overflow-x-auto border-4 border-retro-brown rounded-2xl">
        <table className="w-full text-left bg-retro-cream">
          <thead>
            <tr className="bg-retro-brown text-white font-display tracking-wider text-sm">
              <th className="p-3 border-b-4 border-r-4 border-retro-brown">Graphic</th>
              <th className="p-3 border-b-4 border-r-4 border-retro-brown">Hashtags & Votes</th>
              <th className="p-3 border-b-4 border-r-4 border-retro-brown">Category</th>
              <th className="p-3 border-b-4 border-retro-brown text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y-4 divide-retro-brown">
            {posts.map((post) => (
              <tr key={post.id} className="hover:bg-white transition-colors">
                <td className="p-3 border-r-4 border-retro-brown">
                  <div className="w-16 h-16 rounded-xl border-2 border-retro-brown overflow-hidden shadow-retro-sm mx-auto">
                    <img src={post.image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                </td>
                <td className="p-3 border-r-4 border-retro-brown">
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <input 
                        type="text"
                        value={editingTags[post.id] || ''}
                        onChange={(e) => setEditingTags(prev => ({ ...prev, [post.id]: e.target.value }))}
                        className="flex-1 text-xs font-bold bg-white border-2 border-retro-brown rounded-lg p-2 outline-none shadow-retro-sm"
                        placeholder="Tags..."
                      />
                      <div className="flex items-center gap-1 bg-white border-2 border-retro-brown rounded-lg px-2 shadow-retro-sm">
                        <SafeIcon icon={FiHeart} className="text-red-500 fill-red-500 text-xs" />
                        <input 
                          type="number"
                          value={editingVotes[post.id] || 0}
                          onChange={(e) => setEditingVotes(prev => ({ ...prev, [post.id]: e.target.value }))}
                          className="w-12 text-xs font-bold outline-none text-center"
                        />
                      </div>
                      <button 
                        onClick={() => handleSaveAll(post.id)}
                        disabled={processingId === post.id}
                        className="bg-retro-lime text-retro-brown border-2 border-retro-brown p-2 rounded-lg shadow-retro-sm hover:shadow-retro transition-all"
                      >
                        <SafeIcon icon={FiSave} />
                      </button>
                    </div>
                  </div>
                </td>
                <td className="p-3 border-r-4 border-retro-brown">
                  <select 
                    value={post.category_id || ''} 
                    onChange={(e) => handleUpdateCategory(post.id, e.target.value)}
                    className="text-xs font-bold bg-white border-2 border-retro-brown rounded-lg p-2 w-full shadow-retro-sm"
                  >
                    <option value="">None</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </td>
                <td className="p-3 text-center">
                  <div className="flex justify-center gap-2">
                    <button onClick={() => handleGenerateAI(post)} className="bg-retro-blue text-white border-2 border-retro-brown p-2 rounded-lg shadow-retro-sm hover:shadow-retro"><SafeIcon icon={FiHash} /></button>
                    <button onClick={() => handleDelete(post.id)} className="bg-white text-red-500 border-2 border-retro-brown p-2 rounded-lg shadow-retro-sm hover:bg-red-500 hover:text-white"><SafeIcon icon={FiTrash2} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}